/**
 * Loads Razorpay's Standard Checkout script on demand (once) and exposes a minimal typed
 * surface for the bits we use. Loading lazily keeps the third-party script off every other
 * page and out of the critical path.
 */

const SCRIPT_SRC = 'https://checkout.razorpay.com/v1/checkout.js'

export interface RazorpaySuccessResponse {
  razorpay_payment_id: string
  razorpay_subscription_id: string
  razorpay_signature: string
}

export interface RazorpayOptions {
  key: string
  amount: number
  currency: string
  name: string
  description?: string
  subscription_id: string
  handler: (response: RazorpaySuccessResponse) => void
  prefill?: { name?: string; email?: string; contact?: string }
  notes?: Record<string, string>
  theme?: { color?: string }
  modal?: { ondismiss?: () => void }
}

export interface RazorpayInstance {
  open: () => void
  on: (
    event: 'payment.failed',
    handler: (response: { error: { description?: string } }) => void
  ) => void
}

type RazorpayConstructor = new (options: RazorpayOptions) => RazorpayInstance

declare global {
  interface Window {
    Razorpay?: RazorpayConstructor
  }
}

let loader: Promise<RazorpayConstructor> | null = null

export function loadRazorpay(): Promise<RazorpayConstructor> {
  if (window.Razorpay) return Promise.resolve(window.Razorpay)
  if (loader) return loader

  loader = new Promise<RazorpayConstructor>((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(`script[src="${SCRIPT_SRC}"]`)
    const script = existing ?? document.createElement('script')
    script.src = SCRIPT_SRC
    script.async = true
    script.addEventListener('load', () => {
      if (window.Razorpay) resolve(window.Razorpay)
      else reject(new Error('Razorpay script loaded but window.Razorpay is missing'))
    })
    script.addEventListener('error', () => {
      loader = null
      reject(new Error('Failed to load the Razorpay Checkout script'))
    })
    if (!existing) document.body.appendChild(script)
  })

  return loader
}
